/**
 * Notification Item Component
 *
 * A single notification item displayed in the notification panel.
 * Shows icon, title, message, time, and dismiss action.
 *
 * @usage
 * <ng-notification-item
 *     [notification]="notification"
 *     (action)="onAction($event)"
 *     (dismiss)="onDismiss($event)"
 * ></ng-notification-item>
 */

import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InAppNotification, NotificationType } from '@newgenus/common';
import { TimeAgoPipe } from '../../../shared/pipes/time-ago.pipe';

@Component({
    selector: 'newgenus-notification-item',
    imports: [CommonModule, MatButtonModule, MatIconModule, MatRippleModule, MatTooltipModule, TimeAgoPipe],
    template: `
        <div
            class="notification-item"
            [class.unread]="!notification.isRead"
            [class.high-priority]="notification.priority === 'high'"
            [class.urgent]="notification.priority === 'urgent'"
            matRipple
            (click)="onAction()"
        >
            <!-- Unread indicator -->
            <div class="unread-indicator" *ngIf="!notification.isRead"></div>

            <!-- Icon -->
            <div class="notification-icon" [ngClass]="getIconColorClass()">
                <mat-icon>{{ getIcon() }}</mat-icon>
            </div>

            <!-- Content -->
            <div class="notification-content">
                <div class="notification-header">
                    <span class="notification-title">{{ notification.title }}</span>
                    <span class="notification-time">{{ notification.createdAt | timeAgo }}</span>
                </div>
                <div class="notification-message">{{ notification.message }}</div>
            </div>

            <!-- Actions -->
            <div class="notification-actions" (click)="$event.stopPropagation()">
                <button mat-icon-button (click)="onDismissClick($event)" class="dismiss-btn" matTooltip="Dismiss">
                    <mat-icon>close</mat-icon>
                </button>
            </div>
        </div>
    `,
    styles: [
        `
            .notification-item {
                display: flex;
                align-items: center;
                padding: 6px 10px;
                padding-left: 18px;
                cursor: pointer;
                position: relative;
                transition: background-color 0.2s;
                min-height: 44px;

                &:hover {
                    background-color: rgba(0, 0, 0, 0.04);

                    .dismiss-btn {
                        opacity: 1;
                    }
                }

                &.unread {
                    background-color: rgba(33, 150, 243, 0.06);
                }

                &.high-priority {
                    border-left: 3px solid #ff9800;
                    padding-left: 21px;
                }

                &.urgent {
                    border-left: 3px solid #f44336;
                    padding-left: 21px;
                    background-color: rgba(244, 67, 54, 0.06);
                }
            }

            .unread-indicator {
                position: absolute;
                left: 6px;
                top: 50%;
                transform: translateY(-50%);
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: #2196f3;
            }

            .notification-icon {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 8px;
                flex-shrink: 0;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }

                &.primary {
                    background-color: rgba(33, 150, 243, 0.12);
                    color: #2196f3;
                }

                &.accent {
                    background-color: rgba(255, 152, 0, 0.12);
                    color: #ff9800;
                }

                &.warn {
                    background-color: rgba(244, 67, 54, 0.12);
                    color: #f44336;
                }

                &.success {
                    background-color: rgba(76, 175, 80, 0.12);
                    color: #4caf50;
                }

                &.info {
                    background-color: rgba(33, 150, 243, 0.12);
                    color: #2196f3;
                }
            }

            .notification-content {
                flex: 1;
                min-width: 0;
            }

            .notification-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
            }

            .notification-title {
                font-weight: 500;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .notification-time {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.45);
                white-space: nowrap;
                flex-shrink: 0;
            }

            .notification-message {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.6);
                line-height: 1.2;
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                overflow: hidden;
                margin-top: 1px;
            }

            .notification-actions {
                margin-left: 8px;

                .dismiss-btn {
                    opacity: 0;
                    transition: opacity 0.2s;

                    mat-icon {
                        font-size: 18px;
                        width: 18px;
                        height: 18px;
                    }
                }
            }
        `,
    ],
})
export class NotificationItemComponent {
    @Input() notification!: InAppNotification;
    @Output() action = new EventEmitter<InAppNotification>();
    @Output() dismiss = new EventEmitter<InAppNotification>();

    /**
     * Get the appropriate icon based on notification type
     */
    getIcon(): string {
        if (this.notification.icon) {
            return this.notification.icon;
        }

        // Default icons based on notification type
        const iconMap: Partial<Record<NotificationType, string>> = {
            policy_published: 'policy',
            policy_requires_acknowledgment: 'assignment_turned_in',
            policy_acknowledgment_reminder: 'notification_important',
            policy_deadline_approaching: 'schedule',
            policy_deadline_passed: 'warning',
            policy_updated: 'update',
            policy_revoked: 'cancel',
            policy_expired: 'event_busy',
            approval_requested: 'approval',
            approval_approved: 'check_circle',
            approval_rejected: 'cancel',
            task_assigned: 'assignment',
            task_due_soon: 'schedule',
            task_overdue: 'warning',
            system_announcement: 'campaign',
            security_alert: 'security',
            welcome: 'celebration',
            // Issue tracker v2 (ISSUE-TRACKER-V2-DESIGN.md §3) — the notification docs always
            // carry an explicit `icon`, so this fallback rarely fires, but keeps a sane default
            // for any that don't.
            issue_status_changed: 'swap_horiz',
            issue_archived: 'archive',
            issue_updated: 'edit',
        };

        return iconMap[this.notification.type] || 'notifications';
    }

    /**
     * Get icon color class based on notification settings
     */
    getIconColorClass(): string {
        if (this.notification.iconColor) {
            return this.notification.iconColor;
        }

        // Default colors based on priority
        switch (this.notification.priority) {
            case 'urgent':
                return 'warn';
            case 'high':
                return 'accent';
            default:
                return 'primary';
        }
    }

    /**
     * Handle notification click
     */
    onAction(): void {
        this.action.emit(this.notification);
    }

    /**
     * Handle dismiss click
     */
    onDismissClick(event: Event): void {
        event.stopPropagation();
        this.dismiss.emit(this.notification);
    }
}
